import { defaultPortalPage, type PortalPageSettings } from '@hue/contracts';
import {
  Button,
  Card,
  CardHeader,
  Combobox,
  IconButton,
  Input,
  Modal,
  SegmentedControl,
  Switch,
  Tooltip,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Crop,
  ImagePlus,
  LayoutTemplate,
  Palette,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getApiError } from '../lib/api.js';
import { portalApi, type PortalPublication } from '../lib/portal-api.js';
import { PALETTES } from './appearance.js';
import { brandPrintColors } from './brand.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { PortalCompanyPage } from './portal-company-page.js';
import { centeredFrame, framedStyle, type ImageFrame } from './image-frame.js';
import { ImageFramer } from './image-framer.js';

export function PortalPageEditor({
  draft,
  onChange,
  units,
  services,
  companyKey,
  onBusyChange,
  publicationControls,
}: {
  draft: PortalPublication;
  onChange: (value: PortalPublication) => void;
  units: Array<{ id: string; displayName: string }>;
  services: Array<{ id: string; displayName: string }>;
  companyKey: string;
  onBusyChange: (busy: boolean) => void;
  publicationControls?: ReactNode;
}) {
  const coverInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const page = draft.page ?? defaultPortalPage;
  const update = (change: Partial<PortalPageSettings>) =>
    onChange({ ...draft, page: { ...page, ...change } });
  const [editorUnit, setEditorUnit] = useState('');
  const [publicationOpen, setPublicationOpen] = useState(!draft.unitIds.length);
  const unit =
    units.find((u) => u.id === editorUnit && draft.unitIds.includes(u.id)) ??
    units.find((u) => draft.unitIds.includes(u.id));
  const ids = page.highlights.find((h) => h.unitId === unit?.id)?.serviceIds ?? [];
  const [snapshot, setSnapshot] = useState(draft);
  useEffect(() => {
    const timer = setTimeout(() => setSnapshot(draft), 350);
    return () => clearTimeout(timer);
  }, [draft]);
  const preview = useQuery({
    queryKey: ['company', 'portal-page-preview', snapshot],
    queryFn: () => portalApi.preview(snapshot),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const [device, setDevice] = useState('desktop');
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [uploading, setUploading] = useState(false);
  // Fotos da galeria fora da proporção 4:3, descobertas ao carregar a miniatura.
  const [cropped, setCropped] = useState<Record<string, boolean>>({});
  // Foto da galeria aberta no diálogo de recorte.
  const [framing, setFraming] = useState<string | null>(null);
  const galleryFrame = (id: string) =>
    page.galleryFrames.find((frame) => frame.id === id) ?? centeredFrame;
  function frameGallery(id: string, frame: ImageFrame) {
    update({
      galleryFrames: [...page.galleryFrames.filter((item) => item.id !== id), { id, ...frame }],
    });
  }
  const logos = preview.data?.logoIds;
  const snackbar = useSnackbar();
  function highlights(serviceIds: string[]) {
    if (unit)
      update({
        highlights: [
          ...page.highlights.filter((h) => h.unitId !== unit.id),
          { unitId: unit.id, serviceIds },
        ],
      });
  }
  function move(index: number, delta: number) {
    const next = [...ids];
    [next[index], next[index + delta]] = [next[index + delta]!, next[index]!];
    highlights(next);
  }
  async function upload(selected: File[], gallery = false) {
    // A galeria aceita várias de uma vez, até completar as 4 vagas; a capa usa só a primeira.
    const room = gallery ? 4 - page.galleryIds.length : 1;
    const files = selected.slice(0, room);
    if (!files.length) return;
    if (files.some((file) => file.size > 5 * 1024 * 1024)) {
      snackbar.failure(
        files.length > 1 ? 'Envie somente imagens de até 5 MB.' : 'Envie uma imagem de até 5 MB.',
      );
      return;
    }
    setUploading(true);
    onBusyChange(true);
    const ids: string[] = [];
    let scanned = true;
    try {
      for (const file of files) {
        const media = await portalApi.uploadMedia(file);
        ids.push(media.id);
        scanned &&= media.scanned;
      }
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    } finally {
      // As já preparadas entram mesmo que uma posterior falhe; a pessoa reenvia só a que faltou.
      if (ids.length)
        update(
          gallery
            ? { galleryIds: [...page.galleryIds, ...ids] }
            : { coverId: ids[0]!, coverPositionX: 50, coverPosition: 50, coverZoom: 100 },
        );
      setUploading(false);
      onBusyChange(false);
    }
    if (ids.length === files.length) {
      const many = ids.length > 1;
      const out = selected.length - files.length;
      const left = out
        ? ` Só cabem 4 fotos na galeria; ${out} ${out > 1 ? 'ficaram' : 'ficou'} de fora.`
        : '';
      snackbar.success(
        scanned
          ? `${many ? `${ids.length} imagens preparadas` : 'Imagem preparada'}. Publique as alterações para ${many ? 'mostrá-las' : 'mostrá-la'} no portal.${left}`
          : `${many ? `${ids.length} imagens preparadas` : 'Imagem preparada'}. Antivírus indisponível neste ambiente; ${many ? 'as imagens foram validadas e convertidas' : 'a imagem foi validada e convertida'}.${left}`,
      );
    }
  }
  return (
    <div className="portal-page-editor mt-6 grid items-start gap-6 xl:grid-cols-[minmax(18rem,.7fr)_minmax(0,1.6fr)]">
      <div className="min-w-0 space-y-6">
        <Card>
          <CardHeader title="Modelo da página" icon={<LayoutTemplate className="size-4" />} />
          <div className="space-y-3">
            {(
              [
                {
                  value: 'essential',
                  label: 'Essencial',
                  description: 'Apresentação compacta e lista de serviços em toda a largura.',
                },
                {
                  value: 'visual',
                  label: 'Visual',
                  description: 'Foto de abertura ampla, serviços em cards e galeria.',
                },
                {
                  value: 'units',
                  label: 'Por unidades',
                  description: 'Endereços e contatos primeiro, depois os serviços da unidade.',
                },
                {
                  value: 'showcase',
                  label: 'Vitrine',
                  description: 'Identidade em painel lateral e destaques em grandes cards.',
                },
              ] as const
            ).map((model) => (
              <button
                type="button"
                key={model.value}
                onClick={() => update({ layout: model.value })}
                aria-pressed={page.layout === model.value}
                className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left ${page.layout === model.value ? 'border-brand-500 bg-brand-500/5' : 'border-surface-border'}`}
              >
                <span
                  aria-hidden="true"
                  className={`portal-layout-mini portal-layout-mini-${model.value}`}
                >
                  <i />
                  <i />
                  <i />
                </span>
                <span>
                  <strong className="block">{model.label}</strong>
                  <span className="text-xs text-content-muted">{model.description}</span>
                </span>
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs text-content-muted">
            Trocar de modelo preserva textos, imagens e serviços. Cabeçalho e rodapé do portal
            permanecem iguais.
          </p>
        </Card>
        {publicationControls ? (
          <Card>
            <CardHeader
              title="Publicação na vitrine"
              icon={<LayoutTemplate className="size-4" />}
              action={
                <Button
                  type="button"
                  variant="outline"
                  aria-expanded={publicationOpen}
                  aria-controls="portal-publication-controls"
                  onClick={() => setPublicationOpen((value) => !value)}
                >
                  {publicationOpen ? 'Recolher' : 'Configurar'}
                </Button>
              }
            />
            <div id="portal-publication-controls" hidden={!publicationOpen}>
              {publicationControls}
            </div>
          </Card>
        ) : null}
        <Card>
          <CardHeader title="Identidade da página" icon={<Palette className="size-4" />} />
          <p className="mb-4 text-sm text-content-muted">
            O Símbolo e a Logo principal vêm de Dados da empresa. A cor abaixo acompanha a página da
            empresa e seu agendamento; o visitante escolhe apenas claro ou escuro.
          </p>
          <Combobox
            placeholder="Escolha uma opção"
            label="Cor de destaque da página"
            selectedLabel={PALETTES.find((p) => p.value === page.palette)!.label}
            options={PALETTES.map((p) => ({
              value: p.value,
              label: p.label,
              description: p.description,
            }))}
            onSelect={(choice) => {
              if (choice.kind === 'option')
                update({ palette: choice.value as PortalPageSettings['palette'] });
            }}
          />
          <div
            className="mt-3 flex flex-wrap gap-1"
            role="group"
            aria-label="Escolher paleta da página"
          >
            {PALETTES.map((p) => (
              <Tooltip key={p.value} content={p.label}>
                <button
                  type="button"
                  aria-label={`Usar paleta ${p.label}`}
                  aria-pressed={page.palette === p.value}
                  onClick={() => update({ palette: p.value })}
                  className={`grid size-9 cursor-pointer place-items-center rounded-full border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${page.palette === p.value ? 'border-brand-500' : 'border-transparent'}`}
                >
                  <span
                    aria-hidden="true"
                    className="size-5 rounded-full"
                    style={{ backgroundColor: brandPrintColors[p.value] }}
                  />
                </button>
              </Tooltip>
            ))}
          </div>
          <div className="mt-5">
            <Input
              label="Frase de apresentação"
              value={page.headline}
              maxLength={80}
              onChange={(event) => update({ headline: event.target.value })}
            />
          </div>
          <div className="mt-5 space-y-3">
            <SegmentedControl
              label="Nome no alto da página"
              value={page.nameDisplay}
              onChange={(value) => update({ nameDisplay: value })}
              options={[
                { value: 'text', label: 'Texto' },
                { value: 'logo', label: 'Logo principal' },
              ]}
            />
            {page.nameDisplay === 'text' ? (
              <Input
                label="Nome exibido"
                value={page.title}
                maxLength={60}
                placeholder={preview.data?.displayName}
                hint="Vazio usa o nome da empresa."
                onChange={(event) => update({ title: event.target.value })}
              />
            ) : logos && !logos.main ? (
              <p role="status" className="text-sm text-content-muted">
                Envie a Logo principal em Dados da empresa. Até lá, a página mostra o nome em texto.
              </p>
            ) : null}
            <p className="text-xs text-content-muted">
              Ícone: o Símbolo de Dados da empresa ocupa o quadrado ao lado do nome, no mesmo
              tamanho e formato.
              {logos && !logos.symbol ? ' Sem Símbolo cadastrado, aparece a inicial do nome.' : ''}
            </p>
          </div>
        </Card>
        <Card>
          <CardHeader title="Redes sociais" icon={<LayoutTemplate className="size-4" />} />
          <Switch
            checked={page.showSocialLinks ?? false}
            onChange={(showSocialLinks) => update({ showSocialLinks })}
          >
            Exibir redes sociais no portal
          </Switch>
          <p className="mt-3 text-sm text-content-muted">
            Links para redes sociais podem levar o cliente para fora desta página e interromper um
            agendamento ou uma compra. Considere mantê-los ocultos para favorecer a conclusão por
            aqui.
          </p>
          <p className="mt-3 text-xs text-content-muted">
            Cadastre os perfis em Dados da empresa e Unidades. Quando ativados, os da empresa
            aparecem junto à apresentação e os da unidade junto aos contatos, em todos os modelos.
            Ocultar preserva os cadastros. Publique as alterações para aplicar.
          </p>
        </Card>
        <Card>
          <CardHeader title="Imagens do espaço" icon={<ImagePlus className="size-4" />} />
          <p className="mb-4 text-sm text-content-muted">
            JPEG, PNG ou WebP; até 5 MB, com pelo menos 640 × 240 pixels. A capa aparece nos modelos
            Visual, Por unidades e Vitrine. A galeria aparece no Visual e na Vitrine. As fotos nunca
            são esticadas: ajuste o recorte de cada uma arrastando e com o zoom; o que fica fora da
            moldura não aparece. O arquivo enviado não muda.
          </p>
          <p className="mb-4 text-sm">
            <strong>Capa:</strong> ideal 1920 × 960 pixels (proporção 2:1, horizontal).
          </p>
          <label className="block text-sm font-semibold" htmlFor="portal-cover">
            {page.coverId ? 'Trocar capa' : 'Enviar capa'}
          </label>
          <input
            ref={coverInput}
            id="portal-cover"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={uploading}
            className="hidden"
            onChange={(event) => {
              void upload(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            disabled={uploading}
            onClick={() => coverInput.current?.click()}
          >
            {page.coverId ? 'Trocar imagem da capa' : 'Escolher imagem da capa'}
          </Button>
          {page.coverId ? (
            <>
              <ImageFramer
                key={page.coverId}
                src={portalApi.mediaUrl(companyKey, page.coverId, true)}
                label="Recorte da capa"
                aspect="2/1"
                frame={{ x: page.coverPositionX, y: page.coverPosition, zoom: page.coverZoom }}
                onChange={(frame) =>
                  update({ coverPositionX: frame.x, coverPosition: frame.y, coverZoom: frame.zoom })
                }
              />
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={() => update({ coverId: null })}
              >
                Remover capa
              </Button>
            </>
          ) : null}
          <div className="mt-5">
            <label className="block text-sm font-semibold" htmlFor="portal-gallery">
              Adicionar fotos à galeria ({page.galleryIds.length}/4)
            </label>
            <input
              ref={galleryInput}
              id="portal-gallery"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              disabled={uploading || page.galleryIds.length >= 4}
              className="hidden"
              onChange={(event) => {
                void upload(Array.from(event.target.files ?? []), true);
                event.target.value = '';
              }}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            disabled={uploading || page.galleryIds.length >= 4}
            onClick={() => galleryInput.current?.click()}
          >
            Escolher fotos da galeria
          </Button>
          <p className="mt-2 text-xs text-content-muted">
            Ideal 1600 × 1200 pixels (proporção 4:3, foto de celular na horizontal). Selecione
            várias de uma vez no seletor de arquivos, segurando Ctrl ou ⌘.
          </p>
          <div className="mt-3 space-y-3">
            {page.galleryIds.map((id, index) => (
              <div key={id} className="flex items-center justify-between gap-3">
                <div className="aspect-[4/3] w-28 shrink-0 overflow-hidden rounded-lg">
                  <img
                    src={portalApi.mediaUrl(companyKey, id, true)}
                    alt={`Foto ${index + 1} da galeria`}
                    className="size-full object-cover"
                    style={framedStyle(galleryFrame(id))}
                    onLoad={(event) => {
                      const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
                      const off = Boolean(height) && Math.abs(width / height - 4 / 3) > 0.05;
                      setCropped((current) => ({ ...current, [id]: off }));
                    }}
                  />
                </div>
                <p className="min-w-0 flex-1 text-xs text-content-muted">
                  {cropped[id]
                    ? 'Fora da proporção 4:3: use Ajustar recorte para escolher o que aparece.'
                    : ''}
                </p>
                <IconButton
                  label={`Ajustar recorte da foto ${index + 1}`}
                  onClick={() => setFraming(id)}
                  icon={<Crop />}
                />
                <IconButton
                  label={`Remover foto ${index + 1}`}
                  onClick={() =>
                    update({
                      galleryIds: page.galleryIds.filter((item) => item !== id),
                      galleryFrames: page.galleryFrames.filter((item) => item.id !== id),
                    })
                  }
                  icon={<Trash2 />}
                />
              </div>
            ))}
          </div>
          {framing ? (
            <Modal
              open
              onOpenChange={(open) => !open && setFraming(null)}
              title={`Recorte da foto ${page.galleryIds.indexOf(framing) + 1}`}
              description="Arraste para mover e use o zoom. Só o que fica dentro da moldura aparece na galeria."
              footer={
                <Button type="button" onClick={() => setFraming(null)}>
                  Concluir
                </Button>
              }
            >
              <ImageFramer
                src={portalApi.mediaUrl(companyKey, framing, true)}
                label="Recorte da foto da galeria"
                aspect="4/3"
                frame={galleryFrame(framing)}
                onChange={(frame) => frameGallery(framing, frame)}
              />
            </Modal>
          ) : null}
          {uploading ? (
            <p role="status" className="mt-3 text-sm">
              Preparando imagem…
            </p>
          ) : null}
        </Card>
        <Card>
          <CardHeader title="Serviços em destaque" icon={<Star className="size-4" />} />
          <p className="mb-4 text-sm text-content-muted">
            Escolha até três serviços publicados por unidade e defina a ordem. Eles aparecem nos
            destaques e nos cards das unidades. Nenhum serviço é escolhido automaticamente.
          </p>
          {unit ? (
            <>
              <Combobox
                placeholder="Escolha uma opção"
                label="Unidade dos destaques"
                selectedLabel={unit.displayName}
                options={units
                  .filter((u) => draft.unitIds.includes(u.id))
                  .map((u) => ({ value: u.id, label: u.displayName }))}
                onSelect={(choice) => {
                  if (choice.kind === 'option') setEditorUnit(choice.value);
                }}
              />
              <p className="my-4 text-sm font-semibold">
                {ids.length}/3 destaques · {unit.displayName}
              </p>
              <ol className="divide-y divide-surface-border">
                {ids.map((id, index) => (
                  <li key={id} className="flex items-center justify-between gap-2 py-3">
                    <span className="min-w-0 text-sm">
                      {index + 1}.{' '}
                      {services.find((s) => s.id === id)?.displayName ?? 'Serviço indisponível'}
                    </span>
                    <span className="flex shrink-0 gap-1">
                      <IconButton
                        label={`Subir ${services.find((s) => s.id === id)?.displayName ?? 'Serviço indisponível'}`}
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        icon={<ArrowUp />}
                      />
                      <IconButton
                        label={`Descer ${services.find((s) => s.id === id)?.displayName ?? 'Serviço indisponível'}`}
                        disabled={index === ids.length - 1}
                        onClick={() => move(index, 1)}
                        icon={<ArrowDown />}
                      />
                      <IconButton
                        label={`Remover destaque ${services.find((s) => s.id === id)?.displayName ?? 'Serviço indisponível'}`}
                        onClick={() => highlights(ids.filter((item) => item !== id))}
                        icon={<Trash2 />}
                      />
                    </span>
                  </li>
                ))}
              </ol>
              {ids.length < 3 ? (
                <Combobox
                  placeholder="Escolha uma opção"
                  label="Adicionar serviço em destaque"
                  selectedLabel=""
                  options={services
                    .filter((s) => draft.serviceIds.includes(s.id) && !ids.includes(s.id))
                    .map((s) => ({ value: s.id, label: s.displayName }))}
                  onSelect={(choice) => {
                    if (choice.kind === 'option') highlights([...ids, choice.value]);
                  }}
                />
              ) : (
                <p className="text-xs text-content-muted">
                  Limite de três atingido. Remova um destaque para escolher outro.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-content-muted">
              Selecione uma unidade publicada acima para configurar os destaques.
            </p>
          )}
          <p className="mt-4 text-xs text-content-muted">
            Remover um destaque não despublica o serviço. Ver todos os serviços abre o catálogo
            completo da unidade com busca, preço, duração e acesso aos horários.
          </p>
        </Card>
      </div>
      <section className="min-w-0 xl:sticky xl:top-6" aria-label="Prévia da página">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Prévia · alterações ainda não publicadas</h2>
          <div className="flex flex-wrap gap-3">
            <SegmentedControl
              compact
              label="Tamanho da prévia"
              value={device}
              onChange={setDevice}
              options={[
                { value: 'desktop', label: 'Computador' },
                { value: 'mobile', label: 'Celular' },
              ]}
            />
            <SegmentedControl
              compact
              label="Modo da prévia"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'light', label: 'Claro' },
                { value: 'dark', label: 'Escuro' },
              ]}
            />
          </div>
        </div>
        <div
          className={`public-portal mx-auto overflow-hidden rounded-2xl border border-surface-border ${device === 'mobile' ? 'max-w-[390px]' : ''}`}
          data-mode={mode}
        >
          <header className="border-b border-surface-border bg-surface-card p-5 text-content">
            <strong>HUE•</strong>
            <span className="ml-3 text-xs text-content-muted">Seu próximo cuidado</span>
          </header>
          {preview.isPending ? (
            <LoadingCard label="Preparando prévia" />
          ) : preview.isError ? (
            <ErrorCard message="Não foi possível gerar a prévia. Confira unidades, serviços e imagens selecionados." />
          ) : (
            <PortalCompanyPage
              key={page.layout}
              company={{ ...preview.data, page, description: draft.description }}
              preview
              mode={mode}
              onBook={() =>
                snackbar.success(
                  'A prévia não faz reservas. Publique e abra a página pública para agendar.',
                )
              }
            />
          )}
          <footer className="border-t border-surface-border bg-surface p-5 text-xs text-content-muted">
            HUE• · plataforma de agendamento
          </footer>
        </div>
        <p className="mt-4 text-xs text-content-muted">
          Dados reais, sem reservar horários nem publicar automaticamente. A prévia usa o mesmo
          modelo da página pública.
        </p>
      </section>
    </div>
  );
}
